<template>
  <!-- Notification Dropdown -->
  <VaDropdown :offset="[13, 0]" class="notification-dropdown" stick-to-edges :close-on-content-click="false">
    <template #anchor>
      <VaButton preset="secondary" color="textPrimary">
        <VaBadge overlap>
          <template #text>{{ unreadCount > 0 ? unreadCount : '' }}</template>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            class="size-6"
            stroke="#01ED03"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
            ></path>
          </svg>
        </VaBadge>
      </VaButton>
    </template>
    <VaDropdownContent class="h-full sm:max-w-[420px] sm:h-auto">
      <section class="sm:max-h-[520px] p-4 overflow-auto">
        <VaList class="space-y-1">
          <template v-for="(item, index) in notificationsWithRelativeTime" :key="item.id">
            <VaListItem class="text-base cursor-pointer relative" @click="viewNotification(item)">
              <VaListItemSection icon class="mx-3 p-0">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke-width="1.5"
                  class="size-6"
                  stroke="#01ED03"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    d="M14.857 17.082a23.848 23.848 0 0 0 5.454-1.31A8.967 8.967 0 0 1 18 9.75V9A6 6 0 0 0 6 9v.75a8.967 8.967 0 0 1-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 0 1-5.714 0m5.714 0a3 3 0 1 1-5.714 0"
                  ></path>
                </svg>
              </VaListItemSection>
              <VaListItemSection style="font-size: 13px">
                {{ item.message }}
              </VaListItemSection>
              <VaListItemSection style="font-size: 13px" icon class="small mx-1">
                {{ item.updateTimestamp }}
              </VaListItemSection>
            </VaListItem>
            <div class="flex space-x-2 px-20">
              <button
                style="font-size: 11px"
                class="flex items-center space-x-1 px-2 py-1 text-red-600 border border-red-600 rounded-md text-sm hover:bg-red-100 transition"
                @click="markAsRead(item)"
              >
                <span>Mark As Read</span>
              </button>
              <button
                style="font-size: 11px"
                class="flex items-center space-x-1 px-2 py-1 text-green-600 border border-green-600 rounded-md text-sm hover:bg-green-100 transition"
                @click="viewNotification(item)"
              >
                <span>View Details</span>
              </button>
            </div>
            <VaListSeparator class="mx-3" />
          </template>
        </VaList>
      </section>
    </VaDropdownContent>
  </VaDropdown>

  <!-- Drawer Component for Notification Details -->
  <div
    id="drawer-example"
    style="width: 700px"
    class="p-5 fixed top-0 left-0 z-40 h-screen p-4 overflow-y-auto transition-transform -translate-x-full bg-white w-80 dark:bg-gray-800"
    tabindex="-1"
    aria-labelledby="drawer-label"
    :class="{ 'translate-x-0': selectedNotification }"
  >
    <div class="flex justify-between items-start mb-4">
      <h5 id="drawer-label" class="inline-flex items-center text-base font-semibold text-green-500 dark:text-green-400">
        <svg
          class="w-4 h-4 me-2.5"
          aria-hidden="true"
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            d="M10 .5a9.5 9.5 0 1 0 9.5 9.5A9.51 9.51 0 0 0 10 .5ZM9.5 4a1.5 1.5 0 1 1 0 3 1.5 1.5 0 0 1 0-3ZM12 15H8a1 1 0 0 1 0-2h1v-3H8a1 1 0 0 1 0-2h2a1 1 0 0 1 1 1v4h1a1 1 0 0 1 0 2Z"
          />
        </svg>
        Info
      </h5>
      <div class="flex space-x-2">
        <button
          v-if="alertData.length"
          class="px-4 py-1 text-sm font-medium text-green-500 hover:text-green-700 border border-green-500 hover:border-green-700 rounded-md flex items-center gap-2"
          @click="exportToCSV"
        >
          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span>Export</span>
        </button>
        <button
          type="button"
          aria-controls="drawer-example"
          class="text-red-400 bg-transparent hover:bg-red-200 hover:text-red-900 text-sm w-8 h-8 flex items-center justify-center dark:hover:bg-red-600 dark:hover:text-white"
          @click="closeDrawer"
        >
          <svg class="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
            <path
              stroke="currentColor"
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"
            />
          </svg>
          <span class="sr-only">Close menu</span>
        </button>
      </div>
    </div>

    <div class="mb-6 text-sm text-gray-500 dark:text-gray-400">
      <p><strong>Name:</strong> {{ selectedNotification?.name }}</p>
      <p><strong>Alarm Type:</strong> {{ selectedNotification?.alarm_type }}</p>
      <h3 class="my-5 text-grey-700">{{ selectedNotification?.message }}</h3>
    </div>

    <div>
      <h2 class="text-lg font-bold mb-4">Driver Performance Alerts</h2>
      <table
        v-if="alertData.length && selectedNotification?.alarm_type === 'drivercancellation'"
        class="table-auto w-full border border-gray-300"
      >
        <thead class="bg-gray-100">
          <tr>
            <th class="border px-4 py-2">Driver</th>
            <th class="border px-4 py-2">UUID</th>
            <th class="border px-4 py-2">Start Date</th>
            <th class="border px-4 py-2">Cancellation Rate</th>
            <th class="border px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(alert, index) in alertData"
            :key="index"
            :class="{
              'bg-red-100': alert.cancellation_rate > 0.05,
              'bg-green-100': alert.cancellation_rate <= 0.05,
            }"
          >
            <td class="border px-4 py-2">{{ alert.driver_first_name }} - {{ alert.driver_surname }}</td>
            <td class="border px-4 py-2"><ClickableId type="driver" :value="alert.driver_uuid" /></td>
            <td class="border px-4 py-2">{{ alert.start_date }}</td>
            <td class="border px-4 py-2">{{ (alert.cancellation_rate * 100).toFixed(2) + '%' }}</td>
            <td class="border px-4 py-2">
              {{ alert.cancellation_rate > 0.05 ? 'Above Threshold' : 'Normal' }}
            </td>
          </tr>
        </tbody>
      </table>
      <table
        v-if="alertData.length && selectedNotification?.alarm_type === 'driveracceptance'"
        class="table-auto w-full border border-gray-300"
      >
        <thead class="bg-gray-100">
          <tr>
            <th class="border px-4 py-2">Driver</th>
            <th class="border px-4 py-2">UUID</th>
            <th class="border px-4 py-2">Start Date</th>
            <th class="border px-4 py-2">Acceptance Rate</th>
            <th class="border px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(alert, index) in alertData"
            :key="index"
            :class="{
              'bg-green-100': alert.current_acceptance_rate >= 80,
              'bg-red-100': alert.current_acceptance_rate < 80,
            }"
          >
            <td class="border px-4 py-2">{{ alert.driver_first_name }} - {{ alert.driver_surname }}</td>
            <td class="border px-4 py-2"><ClickableId type="driver" :value="alert.driver_uuid" /></td>
            <td class="border px-4 py-2">{{ alert.start_date }}</td>
            <td class="border px-4 py-2">{{ alert.current_acceptance_rate.toFixed(2) + '%' }}</td>
            <td class="border px-4 py-2">
              {{ alert.current_acceptance_rate >= 80 ? 'Achieved' : 'Failed' }}
            </td>
          </tr>
        </tbody>
      </table>
      <table
        v-if="alertData.length && selectedNotification?.alarm_type === 'drivercompletion'"
        class="table-auto w-full border border-gray-300"
      >
        <thead class="bg-gray-100">
          <tr>
            <th class="border px-4 py-2">Driver</th>
            <th class="border px-4 py-2">UUID</th>
            <th class="border px-4 py-2">Start Date</th>
            <th class="border px-4 py-2">Completion Rate</th>
            <th class="border px-4 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(alert, index) in alertData"
            :key="index"
            :class="{
              'bg-green-100': alert.completion_rate * 100 >= 85,
              'bg-red-100': alert.completion_rate * 100 < 85,
            }"
          >
            <td class="border px-4 py-2">{{ alert.driver_first_name }} - {{ alert.driver_surname }}</td>
            <td class="border px-4 py-2"><ClickableId type="driver" :value="alert.driver_uuid" /></td>
            <td class="border px-4 py-2">{{ alert.start_date }}</td>
            <td class="border px-4 py-2">{{ (alert.completion_rate * 100).toFixed(2) + '%' }}</td>
            <td class="border px-4 py-2">
              {{ alert.completion_rate * 100 >= 85 ? 'Achieved' : 'Failed' }}
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="text-gray-500">No alert data available.</p>
    </div>
  </div>

  <!-- Overlay -->
  <div v-if="selectedNotification" class="fixed inset-0 bg-black bg-opacity-50 z-30" @click="closeDrawer"></div>
</template>

<script setup lang="ts">
import { valtFetch } from '@/utils/api'
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useForm, useToast } from 'vuestic-ui'
import VaIconNotification from '../../../icons/VaIconNotification.vue'
import { useUserStore } from '../../../../stores/user-store'
import { getRegionForApi, appendRegionToUrl, getAuthHeaders } from '../../../../utils/api'
import * as XLSX from 'xlsx'

const selectedNotification = ref<INotification | null>(null)
const alertData = ref([])
const { init } = useToast()

const viewNotification = async (item: INotification) => {
  // Mark notification as read/accessed when viewed
  await markNotificationAsAccessed(item)

  // console.log('Selected notification:', item);
  selectedNotification.value = item

  // Dynamically build API URL from item fields
  const startDate = item.date
  const type = item.type

  const url = appendRegionToUrl(`http://13.247.83.135:953/api/driver/alerts?start_date=${startDate}&type=${type}`)

  try {
    const response = await valtFetch(url, { headers: { ...getAuthHeaders(), 'X-Region': getRegionForApi() } })
    if (!response.ok) throw new Error('Network response was not ok')

    const data = await response.json()
    alertData.value = data.data

    // console.log('Fetched alert data:', alertData.value);
  } catch (error) {
    console.error('Error fetching alert data:', error)
  }
}

const closeDrawer = () => {
  selectedNotification.value = null
}

const exportToCSV = () => {
  try {
    if (!alertData.value.length) {
      init({ message: 'No data to export', color: 'warning' })
      return
    }

    // Format data for export
    const exportData = alertData.value.map((item) => {
      const formattedItem = { ...item }

      // Format rates with percentage
      if ('cancellation_rate' in formattedItem) {
        formattedItem.cancellation_rate = `${(formattedItem.cancellation_rate * 100).toFixed(2)}%`
      }
      if ('current_acceptance_rate' in formattedItem) {
        formattedItem.current_acceptance_rate = `${formattedItem.current_acceptance_rate.toFixed(2)}%`
      }
      if ('completion_rate' in formattedItem) {
        formattedItem.completion_rate = `${(formattedItem.completion_rate * 100).toFixed(2)}%`
      }

      return formattedItem
    })

    // Convert to CSV
    const headers = Object.keys(exportData[0])
    const csvRows = []

    // Add header row
    csvRows.push(headers.join(','))

    // Add data rows
    for (const row of exportData) {
      const values = headers.map((header) => {
        const escaped = ('' + row[header]).replace(/"/g, '\\"')
        return `"${escaped}"`
      })
      csvRows.push(values.join(','))
    }

    const csvString = csvRows.join('\n')

    // Generate file name
    const type = selectedNotification.value?.alarm_type || 'data'
    const date = new Date().toISOString().slice(0, 10)
    const fileName = `${type}_export_${date}.csv`

    // Create download link
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', fileName)
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    init({ message: 'Export successful', color: 'success' })
  } catch (error) {
    // console.error('Export failed:', error);
    init({ message: 'Export failed', color: 'danger' })
  }
}

const { t, locale } = useI18n()
const rtf = new Intl.RelativeTimeFormat(locale.value, { style: 'short' })

// Mark notification as accessed when viewed
const markNotificationAsAccessed = async (item: INotification) => {
  // If already marked as read/accessed, don't do anything
  if (item.read || item.accessed) {
    return
  }

  try {
    const token = localStorage.getItem('token')
    if (!token) {
      console.error('No token found')
      return
    }

    const response = await valtFetch(`http://13.247.83.135:953/api/user/alerts/${item.id}/read`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Region': getRegionForApi(),
      },
    })

    if (!response.ok) {
      throw new Error('Failed to mark notification as read')
    }

    // Update the notification in the array to mark it as read/accessed
    const index = notifications.value.findIndex((n) => n.id === item.id)
    if (index !== -1) {
      notifications.value[index].read = true
      notifications.value[index].accessed = true
    }
  } catch (error) {
    console.error('Error marking notification as accessed:', error)
  }
}

const markAsRead = async (item: INotification) => {
  // console.log(item);

  try {
    const token = localStorage.getItem('token')
    if (!token) {
      console.error('No token found')
      return
    }

    console.log(token)

    const response = await valtFetch(`http://13.247.83.135:953/api/user/alerts/${item.id}/read`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'X-Region': getRegionForApi(),
      },
    })

    if (!response.ok) {
      throw new Error('Failed to mark notification as read')
    }

    // Update the notification in the array to mark it as read/accessed
    const index = notifications.value.findIndex((n) => n.id === item.id)
    if (index !== -1) {
      notifications.value[index].read = true
      notifications.value[index].accessed = true
    }

    init({ message: 'Notification Mark As Read', color: 'success' })
  } catch (error) {
    console.error('Error marking notification as read:', error)
  }
}

interface INotification {
  id: number
  type: string
  message: string
  severity: string
  status: string
  date: string
  name: string
  alarm_type: string
  schedule_start: string
  config: string
  created_at: Date
  schedule_end: string
  telegram: number
  enabled: number
  updateTimestamp: Date
  read?: boolean
  accessed?: boolean
}

const formData = {
  type: '',
  message: '',
  severity: 'low',
  status: 'unresolved',
  date: '',
  notes: '',
}

const notifications = ref<INotification[]>([])

// Computed property to get unread notifications count
const unreadCount = computed(() => {
  return notifications.value.filter((n) => !n.read && !n.accessed).length
})

const TIME_NAMES = {
  second: 1000,
  minute: 1000 * 60,
  hour: 1000 * 60 * 60,
  day: 1000 * 60 * 60 * 24,
  week: 1000 * 60 * 60 * 24 * 7,
  month: 1000 * 60 * 60 * 24 * 30,
  year: 1000 * 60 * 60 * 24 * 365,
}

const getTimeName = (differenceTime: number) => {
  return Object.keys(TIME_NAMES).reduce(
    (acc, key) => (TIME_NAMES[key as keyof typeof TIME_NAMES] < differenceTime ? key : acc),
    'month',
  ) as keyof typeof TIME_NAMES
}

const notificationsWithRelativeTime = computed(() => {
  return notifications.value.map((item, index) => {
    const timeDifference = new Date().getTime() - new Date(item.updateTimestamp).getTime()
    const timeName = getTimeName(timeDifference)

    let separator = false
    const nextItem = notifications.value[index + 1]
    if (nextItem) {
      const nextTimeDifference = new Date().getTime() - new Date(nextItem.updateTimestamp).getTime()
      const nextTimeName = getTimeName(nextTimeDifference)

      separator = timeName !== nextTimeName
    }

    return {
      ...item,
      updateTimestamp: rtf.format(-Math.round(timeDifference / TIME_NAMES[timeName]), timeName),
      separator,
    }
  })
})

const store = useUserStore()

const updateNotification = async () => {
  try {
    await valtFetch(`/api/admin-alerts/${selectedNotification.value.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(formData),
    })
    alert('Notification updated successfully')
    closeDrawer()
  } catch (error) {
    console.error('Error updating notification:', error)
  }
}

const fetchNotifications = async () => {
  try {
    const userResponse = await valtFetch(appendRegionToUrl('http://13.247.83.135:953/api/user/alerts'), {
      method: 'GET',
      headers: {
        ...getAuthHeaders(),
        'X-Region': getRegionForApi(),
      },
    })

    if (!userResponse.ok) {
      throw new Error('Failed to fetch notifications')
    }

    const json = await userResponse.json()
    const items = Array.isArray(json.data) ? json.data : json
    notifications.value = items.map((item: any) => ({
      ...item,
      updateTimestamp: new Date(item.date),
      read: item.read || item.accessed || false,
      accessed: item.accessed || item.read || false,
    }))
  } catch (error) {
    console.error('Error fetching notifications:', error)
  }
}

onMounted(() => {
  fetchNotifications()

  // Listen for report job updates
  const handleReportJobUpdate = (event: CustomEvent) => {
    const { type, message } = event.detail

    // Add to notifications array
    notifications.value.unshift({
      id: Date.now(),
      type: 'report',
      message: message,
      severity: type === 'success' ? 'low' : type === 'error' ? 'high' : 'medium',
      status: 'unresolved',
      date: new Date().toISOString(),
      name: 'Report Job',
      alarm_type: 'report',
      schedule_start: '',
      config: '',
      created_at: new Date(),
      schedule_end: '',
      telegram: 0,
      enabled: 1,
      updateTimestamp: new Date(),
      read: false,
      accessed: false,
    })

    // Show toast notification
    init({
      message: message,
      color: type === 'success' ? 'success' : type === 'error' ? 'danger' : 'info',
    })
  }

  window.addEventListener('report-job-update', handleReportJobUpdate as EventListener)

  onUnmounted(() => {
    window.removeEventListener('report-job-update', handleReportJobUpdate as EventListener)
  })
})
</script>

<style lang="scss" scoped>
.notification-dropdown {
  cursor: pointer;

  .notification-dropdown__icon {
    position: relative;
    display: flex;
    align-items: center;
  }

  .va-dropdown__anchor {
    display: inline-block;
  }
}

.cursor-pointer {
  &:hover {
    background-color: #f0f0f0;
  }
}

.overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 30;
  cursor: pointer;
}
</style>
